/**
 * QueueHealthCard Component
 *
 * Displays Redis queue health metrics including:
 * - Queue depths (current number of items)
 * - Estimated clear times
 *
 * Highlights queues with depth > 1000 in warning color.
 */

import React from 'react';
import type { QueueHealthStats } from '../../types/admin';

interface QueueHealthCardProps {
  queueStats: QueueHealthStats;
}

export function QueueHealthCard({ queueStats }: QueueHealthCardProps) {
  const { metadataQueue, mediaQueue } = queueStats;

  const formatClearTime = (clearTime: string | null): string => {
    return clearTime || 'Unknown';
  };

  const isHighDepth = (depth: number): boolean => depth > 1000;

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Queue Health</h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
        {/* Metadata Queue */}
        <div className={`border rounded-lg p-3 sm:p-4 ${isHighDepth(metadataQueue.depth) ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
          <h4 className="text-sm sm:text-md font-semibold text-gray-700 mb-2 sm:mb-3">Metadata Queue</h4>

          <div className="space-y-2 sm:space-y-3">
            {/* Queue Depth */}
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-600">Depth:</span>
              <span className={`text-base sm:text-lg font-bold ${isHighDepth(metadataQueue.depth) ? 'text-yellow-700' : 'text-gray-900'}`}>
                {metadataQueue.depth.toLocaleString()}
              </span>
            </div>

            {/* Estimated Clear Time */}
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-600">Est. Clear:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                {formatClearTime(metadataQueue.estimatedClearTime)}
              </span>
            </div>
          </div>

          {isHighDepth(metadataQueue.depth) && (
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-yellow-300">
              <p className="text-xs text-yellow-800">
                High queue depth
              </p>
            </div>
          )}
        </div>

        {/* Media Queue */}
        <div className={`border rounded-lg p-3 sm:p-4 ${isHighDepth(mediaQueue.depth) ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200'}`}>
          <h4 className="text-sm sm:text-md font-semibold text-gray-700 mb-2 sm:mb-3">Media Queue</h4>

          <div className="space-y-2 sm:space-y-3">
            {/* Queue Depth */}
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-600">Depth:</span>
              <span className={`text-base sm:text-lg font-bold ${isHighDepth(mediaQueue.depth) ? 'text-yellow-700' : 'text-gray-900'}`}>
                {mediaQueue.depth.toLocaleString()}
              </span>
            </div>

            {/* Estimated Clear Time */}
            <div className="flex justify-between items-center">
              <span className="text-xs sm:text-sm text-gray-600">Est. Clear:</span>
              <span className="text-xs sm:text-sm font-medium text-gray-700">
                {formatClearTime(mediaQueue.estimatedClearTime)}
              </span>
            </div>
          </div>

          {isHighDepth(mediaQueue.depth) && (
            <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-yellow-300">
              <p className="text-xs text-yellow-800">
                High queue depth
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Info Footer */}
      <div className="mt-4 pt-3 sm:pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Auto-refresh every 10 seconds
        </p>
      </div>
    </div>
  );
}
